import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ClipboardEvent as ReactClipboardEvent, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { useRefreshNumberHealth } from '#/entities/number-health'
import type { CustomerDeliverability } from '#/entities/number-health'
import { SHIPMENT_STAGES } from '#/entities/shipment'
import { useOpenShipmentPreview } from '#/widgets/shipment-sidebar'
import { useAuthStore } from '#/shared/lib/auth'
import { useSidePanelStore } from '#/shared/lib/side-panel'
import { cn } from '#/shared/lib/utils'
import {
  AttachIcon,
  CalendarIcon,
  ChevronDownIcon,
  CloseSmallIcon,
} from '#/shared/ui/icons'
import { ImageLightbox } from '#/shared/ui/image-lightbox'
import type { CompletionSource } from '#/shared/ui/note-editor'
import { toast } from '#/shared/ui/sonner'

import type { MessageHitRow } from '../../lib/map-message-hit'
import { withDateSeparators } from '../../lib/map-timeline'
import { timeLabel } from '../../lib/relative-date'
import { sendErrorMessage } from '../../lib/send-error'
import type {
  ConversationTarget,
  OpenImageHandler,
  ThreadMainline,
  TimelineItem,
} from '../../model/conversation'
import { useThreadLightbox } from '../../model/use-thread-lightbox'
import { useOutbox } from '../../model/outbox'
import type { OutboxMessage } from '../../model/outbox'
import {
  registerOpenThread,
  unregisterOpenThread,
} from '../../model/open-threads'
import { useComposer } from '../../model/use-composer'
import { useConversationLiveSync } from '../../model/use-conversation-live-sync'
import {
  useConversationTarget,
  useOpenConversation,
} from '../../model/use-conversation-target'
import { useConversationThread } from '../../model/use-conversation-thread'
import { useInChatSearch } from '../../model/use-in-chat-search'
import type { InChatSearch } from '../../model/use-in-chat-search'
import {
  highlightMessage,
  useMessageHighlight,
} from '../../model/use-message-highlight'
import { useActiveLine } from '../../model/use-phone-numbers'
import { useSendMessage } from '../../model/use-send-message'
import { useSmartCompose } from '../../model/use-smart-compose'
import { useFileDrop } from '../../model/use-file-drop'
import {
  isTextEntry,
  isWithinPhonePanel,
} from '../../model/use-phone-panel-keyboard'
import { useScrollToBottom } from '../../model/use-scroll-to-bottom'
import { useActiveLoads } from '../../model/use-active-loads'
import { CallMessage } from './call-message'
import { ChatDateFilterModal } from './chat-date-filter'
import { ConversationComposer } from './conversation-composer'
import { ConversationHeader } from './conversation-header'
import { MessageBubble } from './message-bubble'
import { OrdersStrip } from './orders-strip'
import { ThreadSearchBar, ThreadSearchResults } from './thread-search'
import { UnblockBar } from './unblock-bar'

/**
 * Date separator that doubles as a jump-to-date entry: tapping it opens the
 * single-date modal seeded at this day; picking a day jumps the thread to that
 * date (capped there) until the user closes the jump. `active` highlights the
 * badge of the day currently jumped to.
 */
function DateSeparator({
  item,
  active,
  onOpen,
}: {
  item: Extract<TimelineItem, { kind: 'date' }>
  active: boolean
  onOpen: (at: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex justify-center py-0.5">
      <button
        type="button"
        onClick={() => onOpen(item.at)}
        aria-label={t('phonePanel.conversation.filterFromDate', {
          date: item.label,
        })}
        className={cn(
          'cursor-pointer rounded-xl px-3 py-1 text-xs font-medium transition',
          active
            ? 'bg-warm-100 text-gray-900'
            : 'bg-warm-100/75 text-warm-500 hover:brightness-95',
        )}
      >
        {item.label}
      </button>
    </div>
  )
}

function TimelineEntry({
  item,
  animate,
  highlighted,
  activeDay,
  onOpenPicker,
  onOpenImage,
}: {
  item: TimelineItem
  /** True only for messages that just arrived, to play the entrance. */
  animate: boolean
  /** True for the entry jumped to — from a notification, or from a search hit
   *  (which can land on a call, matched on its transcript) — to flash it. */
  highlighted: boolean
  /** The jumped-to day (ISO), or null — highlights that day's separator. */
  activeDay: string | null
  /** Open the date picker seeded at a separator's day. */
  onOpenPicker: (at: string) => void
  onOpenImage: OpenImageHandler
}) {
  if (item.kind === 'date') {
    return (
      <DateSeparator
        item={item}
        active={activeDay !== null && dayjs(item.at).isSame(activeDay, 'day')}
        onOpen={onOpenPicker}
      />
    )
  }
  if (item.kind === 'call') {
    return <CallMessage call={item} highlighted={highlighted} />
  }
  return (
    <MessageBubble
      message={item}
      animate={animate}
      highlighted={highlighted}
      onOpenImage={onOpenImage}
    />
  )
}

/**
 * Copy the current selection as plain text only. Bubbles render styled markup
 * (blue surfaces, the pinned timestamp meta, an invisible width-reserving
 * spacer), and the browser's default copy serializes all of that into the
 * clipboard's `text/html` flavor — so pasting a message into a rich-text target
 * drags along the bubble styling. Writing just the selection's text and
 * dropping the html flavor keeps the paste a clean, verbatim line.
 */
function copySelectionAsPlainText(event: ReactClipboardEvent) {
  const selection = window.getSelection()?.toString()
  if (!selection) return
  event.clipboardData.setData('text/plain', selection)
  event.preventDefault()
}

/** Centered status line for the loading / empty thread states. */
function ThreadNotice({ message }: { message: string }) {
  return (
    <div className="flex flex-1 items-center justify-center px-4 text-center">
      <span className="text-xs text-warm-500">{message}</span>
    </div>
  )
}

/**
 * Publish this view's open thread to the realtime registry so inbound SMS/calls
 * refresh it — embedded views (e.g. the sidebar chat tab) are driven by a target
 * prop the global realtime handler can't otherwise see. Keyed on the matching
 * primitives so an inline target object doesn't churn the registration.
 */
function useRegisterOpenThread(target: ConversationTarget | null): void {
  const id = useId()
  const conversationId = target?.conversationId
  const phone = target?.phone
  useEffect(() => {
    if (!conversationId && !phone) return
    registerOpenThread(id, { conversationId, phone })
    return () => unregisterOpenThread(id)
  }, [id, conversationId, phone])
}

/**
 * Refetch the recipient's deliverability every time a chat opens (and on each
 * switch to another number) so the composer's blocked banner reflects the latest
 * status instead of a cached one. Skips the fetch while the panel is closed.
 */
function useRefreshHealthOnOpen(active: boolean, phone: string): void {
  const refresh = useRefreshNumberHealth()
  useEffect(() => {
    if (!active || !phone) return
    refresh()
  }, [active, phone, refresh])
}

/** Measures the frosted header/composer so the scroll content can clear them. */
function useOverlayInsets() {
  const headerRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)
  const [insets, setInsets] = useState({ top: 0, bottom: 0 })

  useLayoutEffect(() => {
    const measure = () =>
      setInsets({
        top: headerRef.current?.offsetHeight ?? 0,
        bottom: composerRef.current?.offsetHeight ?? 0,
      })
    measure()
    const observer = new ResizeObserver(measure)
    if (headerRef.current) observer.observe(headerRef.current)
    if (composerRef.current) observer.observe(composerRef.current)
    return () => observer.disconnect()
  }, [])

  return { headerRef, composerRef, insets }
}

/** Dashed-border prompt shown while a file is dragged over the thread. */
function DropOverlay() {
  const { t } = useTranslation()
  return (
    <div className="pointer-events-none absolute inset-0 z-40 m-3 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-blue-500 bg-blue-500/10 text-blue-500 backdrop-blur-sm">
      <AttachIcon className="size-6" />
      <span className="text-sm font-medium">
        {t('phonePanel.conversation.dropToAttach')}
      </span>
    </div>
  )
}

/** Arrow Up/Down scroll the thread while the panel is open (not while typing). */
function useArrowScroll(ref: { readonly current: HTMLElement | null }) {
  const enabled = useSidePanelStore((state) => state.isOpen)
  useEffect(() => {
    if (!enabled) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      if (isTextEntry(event.target) || !isWithinPhonePanel(event.target)) return
      const el = ref.current
      if (!el) return
      event.preventDefault()
      el.scrollBy({
        top: event.key === 'ArrowDown' ? 120 : -120,
        behavior: 'smooth',
      })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ref, enabled])
}

/** Distance from the bottom still counted as "at the bottom" for sticking. */
const STICK_THRESHOLD_PX = 240

/**
 * Keeps the thread flush against the newest message. A `stuck` flag tracks
 * whether the user is sitting at the bottom; while they are, *any* content
 * growth re-pins to the very bottom — the first load, late-loading images and
 * call players, and the composer growing — so the thread always opens (and
 * stays) all the way down. Scrolling up releases the pin, so reading history
 * and loading older pages aren't yanked back down.
 */
function useStickToBottom(
  ref: RefObject<HTMLElement | null>,
  conversationKey: string,
  paused: boolean,
  contentSignal: number,
  /** False when the thread opens away from its tail (anchored on a search hit):
   *  the highlight owns the viewport, so nothing may pin it to the bottom. */
  startPinned = true,
): () => void {
  const stuck = useRef(true)
  // Read inside the observer so toggling `paused` doesn't re-create it.
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  const startPinnedRef = useRef(startPinned)
  startPinnedRef.current = startPinned

  const pin = useCallback(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [ref])

  // A fresh conversation starts pinned to the bottom again — unless it opened
  // on an anchor, which lands somewhere in the middle of the history instead.
  useEffect(() => {
    stuck.current = startPinnedRef.current
    if (stuck.current) pin()
  }, [conversationKey, pin])

  // Messages load async *after* the conversation opens, so the pin above runs
  // against an empty/short container. Re-pin (before paint, so no flash) each
  // time the loaded message count changes while still stuck — this lands the
  // thread on the newest message once it actually renders. Gated on `stuck` so
  // loading older history (scrolled up) isn't yanked back down, and on `paused`
  // so a notification's highlighted message keeps the viewport.
  useLayoutEffect(() => {
    if (stuck.current && !pausedRef.current) pin()
  }, [contentSignal, pin])

  const onScroll = useCallback(() => {
    const el = ref.current
    if (!el) return
    stuck.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX
  }, [ref])

  // Re-pin whenever the content (or surrounding insets) resize while stuck —
  // unless a highlight scroll owns the viewport (e.g. jumping to a notification's
  // message), so late-loading media can't yank that target back to the bottom.
  useEffect(() => {
    const el = ref.current
    const content = el?.firstElementChild
    if (!el || !content) return
    const observer = new ResizeObserver(() => {
      if (stuck.current && !pausedRef.current) el.scrollTop = el.scrollHeight
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [ref])

  return onScroll
}

/** Distance from the top within which the next (older) page starts loading. */
const LOAD_OLDER_THRESHOLD_PX = 120

interface OlderLoader {
  hasMore: boolean
  isFetchingMore: boolean
  loadMore: () => void
}

/**
 * Infinite history: loads older pages as the thread nears the top, then offsets
 * the scroll position by the height the prepended page added so the jump is
 * invisible — the user keeps reading the same message. Returns a scroll handler
 * to merge with the panel's other scroll logic.
 */
function useLoadOlder(
  ref: RefObject<HTMLElement | null>,
  loader: OlderLoader,
  count: number,
): () => void {
  const prevHeight = useRef<number | null>(null)

  const onScroll = useCallback(() => {
    const el = ref.current
    if (!el || !loader.hasMore || loader.isFetchingMore) return
    if (el.scrollTop > LOAD_OLDER_THRESHOLD_PX) return
    prevHeight.current = el.scrollHeight
    loader.loadMore()
  }, [ref, loader])

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || prevHeight.current === null || loader.isFetchingMore) return
    el.scrollTop += el.scrollHeight - prevHeight.current
    prevHeight.current = null
  }, [ref, count, loader.isFetchingMore])

  return onScroll
}

/** Distance from the bottom within which the next (newer) page starts loading. */
const LOAD_NEWER_THRESHOLD_PX = 120

interface NewerLoader {
  hasNewer: boolean
  isFetchingNewer: boolean
  loadNewer: () => void
}

/**
 * The downward twin of `useLoadOlder`, for a thread that opened away from its
 * tail (a search hit, or a date jump): scrolling toward the bottom pulls the
 * next newer page. Newer items append below the viewport, so — unlike prepending
 * older history — no scroll compensation is needed.
 */
function useLoadNewer(
  ref: RefObject<HTMLElement | null>,
  loader: NewerLoader,
): () => void {
  return useCallback(() => {
    const el = ref.current
    if (!el || !loader.hasNewer || loader.isFetchingNewer) return
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (fromBottom > LOAD_NEWER_THRESHOLD_PX) return
    loader.loadNewer()
  }, [ref, loader])
}

/**
 * Ids of messages that arrived after the thread first loaded, so only those
 * play the entrance animation (the existing history appears instantly).
 */
function useNewMessageIds(
  timeline: ReadonlyArray<TimelineItem>,
): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null)
  const fresh = new Set<string>()
  if (seen.current) {
    for (const item of timeline) {
      if (item.kind !== 'date' && !seen.current.has(item.id)) fresh.add(item.id)
    }
  }
  useEffect(() => {
    // Don't baseline the empty loading timeline — otherwise the whole first
    // page counts as "new" once it arrives and every bubble animates in (and
    // that entrance collides with a notification's highlight flash). Wait for
    // the first populated render so loaded history appears instantly instead.
    if (seen.current === null && timeline.length === 0) return
    const next = new Set<string>()
    for (const item of timeline) {
      if (item.kind !== 'date') next.add(item.id)
    }
    seen.current = next
  }, [timeline])
  return fresh
}

/** Id of the newest (tail) message/call in the timeline, or null. */
function tailId(timeline: ReadonlyArray<TimelineItem>): string | null {
  for (let index = timeline.length - 1; index >= 0; index--) {
    const item = timeline[index]
    if (item.kind !== 'date') return item.id
  }
  return null
}

/**
 * Pin the thread to the bottom whenever a new entry lands at its tail — a live
 * inbound message, another tab's send, an own send, or a just-logged call.
 * Stick-to-bottom only re-pins while the user already sits near the bottom;
 * this covers the rest, matching chat convention: fresh activity snaps the
 * thread down. Older history prepends (never the tail), so paging up is
 * unaffected. Paused while a notification highlight owns the viewport.
 */
function useAutoScrollOnNewTail(
  ref: RefObject<HTMLElement | null>,
  timeline: ReadonlyArray<TimelineItem>,
  newIds: ReadonlySet<string>,
  /** Message being flashed (a notification / search jump), or null. */
  highlightId: string | null,
  /** Search hit the thread opened on, or undefined for a normal open. */
  anchorId: string | undefined,
): void {
  const paused = highlightId !== null || anchorId !== undefined
  const last = tailId(timeline)
  const isNew = last !== null && newIds.has(last)
  useLayoutEffect(() => {
    if (!isNew || paused) return
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [last, isNew, paused, ref])
}

interface ThreadScrollProps {
  scrollRef: RefObject<HTMLDivElement | null>
  onScroll: () => void
  insets: { top: number; bottom: number }
  isLoading: boolean
  isEmpty: boolean
  timeline: ReadonlyArray<TimelineItem>
  newIds: ReadonlySet<string>
  /** Message id to flash (jumped to from a notification), or null. */
  highlightId: string | null
  /** The jumped-to day (ISO), or null — highlights that day's separator. */
  activeDay: string | null
  /** Open the date picker seeded at a separator's day. */
  onOpenPicker: (at: string) => void
  onOpenImage: OpenImageHandler
}

/** The scrollable thread body: loading/empty notices and the timeline. */
function ThreadScroll({
  scrollRef,
  onScroll,
  insets,
  isLoading,
  isEmpty,
  timeline,
  newIds,
  highlightId,
  activeDay,
  onOpenPicker,
  onOpenImage,
}: ThreadScrollProps) {
  const { t } = useTranslation()
  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      onCopy={copySelectionAsPlainText}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
    >
      <div
        className="flex min-h-full flex-col justify-end gap-4 px-4"
        style={{
          paddingTop: insets.top + 12,
          paddingBottom: insets.bottom + 12,
        }}
      >
        {isLoading && (
          <ThreadNotice message={t('phonePanel.conversation.loading')} />
        )}
        {isEmpty && (
          <ThreadNotice message={t('phonePanel.conversation.empty')} />
        )}
        {timeline.map((item) => (
          <TimelineEntry
            key={item.id}
            item={item}
            animate={newIds.has(item.id)}
            highlighted={item.id === highlightId}
            activeDay={activeDay}
            onOpenPicker={onOpenPicker}
            onOpenImage={onOpenImage}
          />
        ))}
      </div>
    </div>
  )
}

/** Optimistic outbox messages → outgoing timeline entries. */
function outboxToEntries(
  messages: ReadonlyArray<OutboxMessage>,
): TimelineItem[] {
  return messages.map((message) => ({
    kind: 'message',
    id: message.id,
    at: message.time,
    direction: 'out',
    text: message.text,
    meta: timeLabel(message.time),
    status: message.status,
    // Shown in full in the failed "!" marker's popover.
    errorDetail: message.error,
    noResend: message.noResend,
    photos: message.photos.length > 0 ? message.photos : undefined,
    route: message.route,
    mainline: message.mainline,
  }))
}

function entryAt(entry: TimelineItem): string {
  return entry.kind === 'date' ? '' : entry.at
}

/** Signature matching an optimistic overlay message to its server copy. */
function dupSignature(entry: TimelineItem): string | null {
  if (entry.kind !== 'message' || entry.direction !== 'out') return null
  const text = entry.text.trim()
  if (text) return `text:${text}`
  return entry.photos?.length ? 'media' : null
}

/** How many overlay sends carry each signature. */
function overlayCounts(
  overlay: ReadonlyArray<TimelineItem>,
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const entry of overlay) {
    // A rejected send (`noResend`) was never queued, so it has no server echo —
    // counting it would hide a genuine server message sharing the same text.
    if (entry.kind === 'message' && entry.noResend) continue
    const sig = dupSignature(entry)
    if (!sig) continue
    counts.set(sig, (counts.get(sig) ?? 0) + 1)
  }
  return counts
}

/**
 * Server entry ids the overlay already represents. Each optimistic send's echo
 * comes back from the server with its own id and timestamp, so we match echoes
 * to bubbles by signature + count: for every overlay bubble carrying a
 * signature, hide one server entry that shares it. Matching is deliberately
 * timestamp-free — the backend sometimes echoes a message with a missing/zeroed
 * `occurred_at` (it renders as 12:00 AM), and any comparison against the local
 * send clock would then fail to recognize the echo and let it render alongside
 * the bubble, doubling every send. Newest server copies are hidden first so a
 * genuinely older message sharing the same text (e.g. yesterday's identical
 * line) survives rather than the fresh echo.
 */
function overlayDupIds(
  server: ReadonlyArray<TimelineItem>,
  overlay: ReadonlyArray<TimelineItem>,
): Set<string> {
  const remaining = overlayCounts(overlay)
  const hidden = new Set<string>()
  const newestFirst = [...server].sort((a, b) =>
    entryAt(b).localeCompare(entryAt(a)),
  )
  for (const entry of newestFirst) {
    const sig = dupSignature(entry)
    const left = sig ? (remaining.get(sig) ?? 0) : 0
    if (!sig || left === 0) continue
    remaining.set(sig, left - 1)
    hidden.add(entry.id)
  }
  return hidden
}

/**
 * Merge the server thread with the optimistic overlay, ordered by time. As many
 * server copies as the overlay is showing are hidden — newest first, so a
 * refetch never duplicates the user's own sent bubbles, yet older messages that
 * happen to share the same text are kept.
 */
function mergeEntries(
  server: ReadonlyArray<TimelineItem>,
  overlay: ReadonlyArray<TimelineItem>,
): TimelineItem[] {
  if (overlay.length === 0) return [...server]
  const hidden = overlayDupIds(server, overlay)
  const deduped = server.filter((entry) => !hidden.has(entry.id))
  return [...deduped, ...overlay].sort((a, b) =>
    entryAt(a).localeCompare(entryAt(b)),
  )
}

/** Merge server + overlay entries, add date separators, and report emptiness. */
function useThreadTimeline(
  thread: { entries: ReadonlyArray<TimelineItem>; isLoading: boolean },
  outboxMessages: ReadonlyArray<OutboxMessage>,
  t: ReturnType<typeof useTranslation>['t'],
) {
  const merged = useMemo(
    () => mergeEntries(thread.entries, outboxToEntries(outboxMessages)),
    [thread.entries, outboxMessages],
  )
  const timeline = useMemo(() => withDateSeparators(merged, t), [merged, t])
  return { timeline, isEmpty: !thread.isLoading && merged.length === 0 }
}

/**
 * Sender identity of an optimistic send, captured at send time because the
 * server echo that carries it is hidden by the overlay merge until the thread
 * reloads. The tap-reveal `route` mirrors `smsRoute` (the name leads only on a
 * mainline send); the photo-viewer `captionParts` mirror `photoCaption` — the
 * name leads whenever known (DEV-700).
 */
function outboundSender(
  user: { first_name?: string | null; last_name?: string | null } | null,
  activeLinePhone: string | undefined,
  mainline?: ThreadMainline,
): { route?: string; captionParts: string[] } {
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
  const line = mainline?.phone ?? activeLinePhone ?? ''
  const route = mainline ? [name, line].filter(Boolean).join(' · ') : line
  return {
    route: route || undefined,
    captionParts: [name, line].filter(Boolean),
  }
}

/**
 * Wires the thread, sending line, and composer into one optimistic send: the
 * bubble shows instantly (status `sending`), then flips to `sent` on success
 * or to a kept-in-thread `failed` bubble on failure, with the backend's
 * rejection reason shown beneath it. The composer resets immediately because
 * `onSend` resolves before the network call settles.
 */
/**
 * Re-point the open chat when the send response reports a different thread than
 * the one currently shown. Happens when the panel was opened by number (no id
 * yet) and the backend resolves/creates the conversation on send. Only the
 * URL-driven panel navigates; embedded views (shipment sidebar) manage their
 * own target and pass `enabled: false`.
 */
interface ComposerRedirect {
  conversationId?: string
  enabled: boolean
}

function composerRedirect(
  target: ConversationTarget | null,
  embedded: boolean,
): ComposerRedirect {
  return { conversationId: target?.conversationId, enabled: !embedded }
}

function useConversationComposer(
  toNumber: string,
  mainline: ThreadMainline | undefined,
  redirect: ComposerRedirect,
) {
  const { t } = useTranslation()
  const { active } = useActiveLine()
  const composer = useComposer()
  const { send, isSending } = useSendMessage()
  const openConversation = useOpenConversation()
  const { messages, add, markSent, markFailed, clear } = useOutbox(toNumber)
  const user = useAuthStore((state) => state.user)
  const { route: outboundRoute, captionParts } = outboundSender(
    user,
    active?.phone,
    mainline,
  )

  // Drop a thread's optimistic bubbles when it closes; reopening loads them
  // for real from the server. Rejected sends are the exception — the store
  // keeps those, since no server copy exists to reload them from.
  useEffect(() => () => clear(toNumber), [toNumber, clear])

  const onSend = (): Promise<boolean> => {
    // `plainText` is the flattened body the SMS actually carries.
    const { plainText, attachments } = composer
    if (plainText.trim().length === 0 && attachments.length === 0) {
      return Promise.resolve(false)
    }
    // A mainline thread sends from the shared line; the personal-line text
    // capability gate only applies when sending from the user's own line.
    if (!mainline && active && !active.canText) {
      toast.error(t('phonePanel.lineCannotText'))
      return Promise.resolve(false)
    }
    const id = crypto.randomUUID()
    const time = new Date().toISOString()
    add(toNumber, {
      id,
      text: plainText,
      files: attachments.map((attachment) => attachment.file),
      time,
      route: outboundRoute,
      photoCaption: [...captionParts, timeLabel(time)].join(' - '),
      mainline: mainline !== undefined,
    })
    void send({
      userPhoneId: mainline?.id ?? active?.id ?? null,
      toNumber,
      text: plainText,
      attachments,
    }).then(({ ok, conversationId, errorCode, errorMessage }) => {
      if (ok) {
        markSent(toNumber, id)
        if (
          redirect.enabled &&
          conversationId &&
          conversationId !== redirect.conversationId
        ) {
          openConversation({ conversationId, phone: toNumber })
        }
      } else {
        // The failed bubble stays in the thread (iMessage-style) rather than
        // being undone; the reason lives in its "!" marker popover — no toast,
        // the bubble is right in front of the user. `noResend` marks a backend
        // rejection: it was never queued, so no server copy exists (kept
        // across thread closes, excluded from the echo dedupe).
        markFailed(toNumber, id, {
          error: sendErrorMessage(t, { errorCode, errorMessage }),
          noResend: errorCode !== null,
        })
      }
    })
    return Promise.resolve(true)
  }

  return { composer, onSend, isSending, outboxMessages: messages }
}

/** Frosted top overlay: contact header, divider, and the active-orders strip. */
function ThreadHeader({
  headerRef,
  thread,
  conversationId,
  activeLoads,
  search,
  onBack,
}: {
  headerRef: RefObject<HTMLDivElement | null>
  thread: ReturnType<typeof useConversationThread>
  conversationId?: string
  activeLoads: ReturnType<typeof useActiveLoads>
  /** In-thread search; its box replaces the contact header while open. */
  search: InChatSearch
  onBack?: () => void
}) {
  const openPreview = useOpenShipmentPreview()
  const { loads, isLoading, hasCustomer } = activeLoads
  // Offer "Open order" in the header menu only when it's unambiguous — a single
  // order. With several, the active-orders strip below is the way in.
  const orderId = loads.length === 1 ? loads[0].id : null
  return (
    // No backdrop-filter on the wrapper itself: nesting one backdrop-filter
    // inside another cancels the child's blur, so the frost lives on the header
    // and strip-head separately, leaving the orders dropdown free to blur.
    <div ref={headerRef} className="absolute inset-x-0 top-0 z-20">
      <div className="bg-white/70 backdrop-blur-md">
        {search.open ? (
          <ThreadSearchBar search={search} />
        ) : (
          <ConversationHeader
            contact={{
              name: thread.name,
              phone: thread.phone,
              line: '',
              activeLoads: loads.length,
            }}
            customer={thread.customer}
            menuTarget={{ conversationId, phone: thread.toNumber }}
            starred={thread.starred}
            blocked={thread.blocked}
            onOpenOrder={
              orderId
                ? () => openPreview(SHIPMENT_STAGES.ORDER, orderId)
                : undefined
            }
            onSearch={conversationId ? search.toggle : undefined}
            onBack={onBack}
          />
        )}
      </div>
      {/* The orders strip is thread context, not search chrome — hide it while
          searching so the hit list gets the height. */}
      {!search.open && (
        <OrdersStrip
          loads={loads}
          isLoading={isLoading}
          hasCustomer={hasCustomer}
        />
      )}
    </div>
  )
}

/** Floating pill that jumps the thread to the newest message when scrolled up. */
function ScrollToBottomButton({
  onClick,
  visible,
  bottom,
}: {
  onClick: () => void
  visible: boolean
  bottom: number
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('phonePanel.conversation.scrollToBottom')}
      style={{ bottom }}
      className={cn(
        'absolute left-1/2 z-30 flex size-9 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/70 text-gray-500 shadow-popover backdrop-blur-md transition-all duration-200',
        visible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-2 opacity-0',
      )}
    >
      <ChevronDownIcon className="size-4" />
    </button>
  )
}

/**
 * Banner shown while the thread is filtered to a jumped-to date: it names the
 * date and offers an ✕ to return to the normal (live, latest) chat. Sits just
 * below the frosted header overlay. A search-hit anchor deliberately shows no
 * banner — the jump-to-bottom pill already exits it (see `useBackToLatest`).
 */
function JumpBanner({
  active,
  label,
  top,
  onClose,
}: {
  /** False while the thread is live — the banner renders nothing. */
  active: boolean
  label: string
  top: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  if (!active) return null
  return (
    <div
      style={{ top: top + 8 }}
      className="absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-gray-200 bg-white/80 py-1 pr-1 pl-3 text-xs font-medium text-gray-900 shadow-popover backdrop-blur-md"
    >
      <CalendarIcon className="size-3.5 text-gray-500" />
      <span>{label}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('phonePanel.conversation.jumpToLatest')}
        className="flex size-5 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900"
      >
        <CloseSmallIcon className="size-3.5" />
      </button>
    </div>
  )
}

/** Bottom dock: the composer, or the unblock bar for a blocked counterparty. */
function ThreadComposerDock({
  dockRef,
  blocked,
  conversationId,
  toNumber,
  contactName,
  customer,
  optedOut,
  composer,
  onSend,
  sending,
  mainline,
  completionSource,
}: {
  dockRef: RefObject<HTMLDivElement | null>
  blocked: boolean
  conversationId?: string
  toNumber: string
  contactName?: string
  customer: CustomerDeliverability | null | undefined
  optedOut: boolean
  composer: ReturnType<typeof useConversationComposer>['composer']
  onSend: () => Promise<boolean>
  sending: boolean
  mainline?: ThreadMainline
  completionSource?: CompletionSource
}) {
  return (
    <div
      ref={dockRef}
      className="absolute inset-x-0 bottom-0 z-20 bg-white/70 backdrop-blur-md"
    >
      {blocked ? (
        <UnblockBar target={{ conversationId, phone: toNumber }} />
      ) : (
        <ConversationComposer
          composer={composer}
          onSend={onSend}
          sending={sending}
          mainline={mainline}
          toNumber={toNumber}
          contactName={contactName}
          customer={customer}
          optedOut={optedOut}
          autoFocus
          completionSource={completionSource}
        />
      )}
    </div>
  )
}

/**
 * Local jump-to-date anchor for the open thread. Picking a date seeds the
 * timeline query (as `to_date`) so the first page ends on that day, and the
 * view scrolls there; the user can then page older (up) and newer (down) from
 * it. Resets whenever a different thread is opened so a jump never leaks across
 * chats.
 */
function useJumpToDate(target: ConversationTarget | null) {
  const threadKey = target?.conversationId ?? target?.phone ?? ''
  const [jumpDate, setJumpDate] = useState<string | undefined>(undefined)
  // The day the modal is seeded at (the tapped separator); null = modal closed.
  const [seed, setSeed] = useState<string | null>(null)
  useEffect(() => {
    setJumpDate(undefined)
    setSeed(null)
  }, [threadKey])
  const openPicker = useCallback((at: string) => setSeed(at), [])
  const closePicker = useCallback(() => setSeed(null), [])
  const apply = useCallback((date: Date) => {
    // End-of-day so the whole picked day is included as the anchor page.
    setJumpDate(dayjs(date).endOf('day').toISOString())
    setSeed(null)
  }, [])
  const clear = useCallback(() => setJumpDate(undefined), [])
  return {
    jumpDate,
    jumpActive: jumpDate != null,
    /** The jumped-to day (ISO) or null, for separator highlighting. */
    activeDay: jumpDate ?? null,
    /** Human label for the jumped-to date, shown in the exit banner. */
    jumpLabel: jumpDate ? dayjs(jumpDate).format('MMM D, YYYY') : '',
    seed,
    openPicker,
    closePicker,
    apply,
    clear,
  }
}

/**
 * The search hit the open thread is anchored on, if any. Only a URL-opened
 * thread can be anchored — the embedded chat tab (shipment sidebar) drives its
 * own target and always opens live.
 */
function useThreadAnchor(targetOverride: ConversationTarget | undefined) {
  const { target: urlTarget, clearAnchor } = useConversationTarget()
  return {
    urlTarget,
    anchorId: targetOverride ? undefined : urlTarget?.messageId,
    clearAnchor,
  }
}

/**
 * What the jump-to-bottom pill does. On an anchored thread "the bottom" isn't
 * the newest message — it's the end of the page loaded around the search hit —
 * so the pill drops the anchor and reloads live instead. That's also the exit
 * from a search jump, which is why an anchored thread shows no banner: the pill
 * is already there and already means "back to the latest".
 */
function useBackToLatest(
  anchorId: string | undefined,
  clearAnchor: () => void,
  scrollToBottom: () => void,
): () => void {
  return useCallback(() => {
    if (anchorId) clearAnchor()
    else scrollToBottom()
  }, [anchorId, clearAnchor, scrollToBottom])
}

/**
 * A thread opened from a search hit carries the hit as its anchor: the timeline
 * is fetched around that item, so queue the same id for the highlight flash that
 * notification jumps use — it scrolls the bubble into view once it renders.
 */
function useAnchorHighlight(target: ConversationTarget | null): void {
  const conversationId = target?.conversationId
  const messageId = target?.messageId
  useEffect(() => {
    if (conversationId && messageId) highlightMessage(conversationId, messageId)
  }, [conversationId, messageId])
}

/**
 * Composes the thread's scroll behaviours into a single handler: stick-to-bottom
 * keeps the newest message in view (paused while a highlight owns the viewport),
 * and history pages in as the user scrolls — older going up, and newer going
 * down once the thread opened away from its tail. Keying stick-to-bottom on the
 * jump date makes a jump land at the bottom of the date-capped page (its latest
 * message); the exit banner returns to live. An anchored open (a search hit)
 * never sticks: the flashed message keeps the viewport until the anchor is
 * dropped.
 */
function useThreadScroll(opts: {
  ref: RefObject<HTMLElement | null>
  thread: ReturnType<typeof useConversationThread>
  timelineLength: number
  jumpDate: string | undefined
  /** Timeline item the thread opened on, or undefined for a normal open. */
  anchorId: string | undefined
  highlightId: string | null
  handleScroll: () => void
}): () => void {
  const {
    ref,
    thread,
    timelineLength,
    jumpDate,
    anchorId,
    highlightId,
    handleScroll,
  } = opts
  const loadOlderOnScroll = useLoadOlder(ref, thread, timelineLength)
  const loadNewerOnScroll = useLoadNewer(ref, thread)
  const stickOnScroll = useStickToBottom(
    ref,
    `${thread.toNumber}::${jumpDate ?? ''}::${anchorId ?? ''}`,
    highlightId !== null || anchorId !== undefined,
    timelineLength,
    anchorId === undefined,
  )
  return useCallback(() => {
    handleScroll()
    stickOnScroll()
    loadOlderOnScroll()
    loadNewerOnScroll()
  }, [handleScroll, stickOnScroll, loadOlderOnScroll, loadNewerOnScroll])
}

/**
 * In-thread search plus the jump that opens one of its hits: picking a hit just
 * re-anchors the thread on that message (`chatAt`), so the timeline reloads
 * around it. Offered only for the panel's own id-scoped thread — the embedded
 * chat tab supplies its own chrome and doesn't own the URL.
 */
function useThreadSearch(
  target: ConversationTarget | null,
  embedded: boolean,
  active: boolean,
) {
  const conversationId = embedded ? undefined : target?.conversationId
  const search = useInChatSearch(conversationId, active)
  const openConversation = useOpenConversation()
  const phone = target?.phone
  const { close } = search
  const openHit = useCallback(
    (hit: MessageHitRow) => {
      openConversation({ conversationId, phone, messageId: hit.id })
      close()
    },
    [openConversation, conversationId, phone, close],
  )
  return { search, openHit }
}

interface ConversationViewProps {
  onBack?: () => void
  /** Drive a specific conversation instead of the URL-selected one (embedded
   *  use, e.g. the shipment sidebar chat tab). */
  target?: ConversationTarget
  /** Hide the contact/orders header — the embedder supplies its own context. */
  hideHeader?: boolean
  /** Extra classes for the root (e.g. override the chat surface colour). */
  className?: string
}

/** Full conversation thread shown when a chat is opened from the list. */
export function ConversationView({
  onBack,
  target: targetOverride,
  hideHeader = false,
  className,
}: ConversationViewProps) {
  const { t } = useTranslation()
  const { urlTarget, anchorId, clearAnchor } = useThreadAnchor(targetOverride)
  const target = targetOverride ?? urlTarget
  // Embedded callers (shipment sidebar) stay live; the URL-driven panel thread
  // only fetches while open (it stays mounted + `chat` param retained on close).
  const panelOpen = useSidePanelStore((state) => state.isOpen)
  const threadActive = targetOverride != null || panelOpen
  useRegisterOpenThread(target)
  useConversationLiveSync(target?.conversationId)
  const {
    jumpDate,
    jumpActive,
    activeDay,
    jumpLabel,
    seed,
    openPicker,
    closePicker,
    apply,
    clear,
  } = useJumpToDate(target)
  const thread = useConversationThread(target, threadActive, jumpDate)
  const { search, openHit } = useThreadSearch(
    target,
    targetOverride !== undefined,
    threadActive,
  )
  useRefreshHealthOnOpen(threadActive, thread.toNumber)
  // An anchored open owns its scroll position — don't pin it to the tail.
  const { ref, showButton, handleScroll, scrollToBottom } =
    useScrollToBottom<HTMLDivElement>(anchorId === undefined)
  const backToLatest = useBackToLatest(anchorId, clearAnchor, scrollToBottom)
  const { openImage, lightboxProps } = useThreadLightbox()
  const { headerRef, composerRef, insets } = useOverlayInsets()
  const { composer, onSend, isSending, outboxMessages } =
    useConversationComposer(
      thread.toNumber,
      thread.mainline,
      composerRedirect(target, targetOverride !== undefined),
    )
  const { dragging, dropHandlers } = useFileDrop(composer.addFiles)
  const activeLoads = useActiveLoads(thread.customerId)
  const { timeline, isEmpty } = useThreadTimeline(thread, outboxMessages, t)
  const completionSource = useSmartCompose(timeline)
  const newIds = useNewMessageIds(timeline)
  useAnchorHighlight(target)
  const highlightId = useMessageHighlight(
    ref,
    target,
    timeline,
    thread.isLoading,
    anchorId !== undefined,
  )
  useAutoScrollOnNewTail(ref, timeline, newIds, highlightId, anchorId)
  const onScroll = useThreadScroll({
    ref,
    thread,
    timelineLength: timeline.length,
    jumpDate,
    anchorId,
    highlightId,
    handleScroll,
  })
  useArrowScroll(ref)

  return (
    <div
      className={cn('relative flex h-full flex-col bg-paper-avatar', className)}
      {...dropHandlers}
    >
      <ThreadScroll
        scrollRef={ref}
        onScroll={onScroll}
        insets={insets}
        isLoading={thread.isLoading}
        isEmpty={isEmpty}
        timeline={timeline}
        newIds={newIds}
        highlightId={highlightId}
        activeDay={activeDay}
        onOpenPicker={openPicker}
        onOpenImage={openImage}
      />
      {!hideHeader && (
        <ThreadHeader
          headerRef={headerRef}
          thread={thread}
          conversationId={target?.conversationId}
          activeLoads={activeLoads}
          search={search}
          onBack={onBack}
        />
      )}
      <ThreadSearchResults
        search={search}
        top={insets.top}
        bottom={insets.bottom}
        onOpen={openHit}
      />
      <JumpBanner
        active={jumpActive}
        label={jumpLabel}
        top={insets.top}
        onClose={clear}
      />
      <ChatDateFilterModal seed={seed} onApply={apply} onClose={closePicker} />
      <ScrollToBottomButton
        onClick={backToLatest}
        visible={showButton}
        bottom={insets.bottom + 12}
      />
      <ThreadComposerDock
        dockRef={composerRef}
        blocked={thread.blocked}
        conversationId={target?.conversationId}
        toNumber={thread.toNumber}
        contactName={thread.name}
        customer={thread.customer}
        optedOut={thread.optedOut}
        composer={composer}
        onSend={onSend}
        sending={isSending}
        mainline={thread.mainline}
        completionSource={completionSource}
      />

      {dragging && <DropOverlay />}

      <ImageLightbox {...lightboxProps} />
    </div>
  )
}
